/* Bridal Quote Studio™ — premium quote layout editor.

  This page is intentionally STANDALONE (its own route `/app/settings/quote-studio`)
  to keep `Settings.jsx` lean and to make this premium-gated feature easy
  to upsell as a single-purpose page.

  Architecture:
    - One template per user. Backend stores at `/api/quote-template/me`.
    - 5 sections (Cover, Chi sono, Portfolio, Preventivo, Contatti).
      Each section is a self-contained block in the form.
    - Sections are draggable using native HTML5 drag&drop (no extra lib).
    - Image uploads go through `/api/quote-template/me/upload` which writes
      to Cloudflare R2 and returns an `r2://…` reference. We render previews
      by asking the backend for a signed URL on save round-trip (the GET
      returns `_r()`-resolved URLs in the public payload, but the EDITOR
      sees the raw refs — we render previews from base64 data URLs locally
      until they're persisted).
    - Plan gating: any plan that is NOT `ai_manager` sees a teaser screen
      with disabled inputs + "Upgrade" CTA. Backend also enforces.
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowLeft, Save, Image as ImageIcon, GripVertical, Trash2,
  Plus, ChevronUp, ChevronDown, Eye, EyeOff, ExternalLink, Loader2, Lock,
  Monitor, Pencil, RefreshCw, Radio,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { writePreviewSnapshot } from "@/pages/QuoteStudioPreview";

const SECTION_META = {
  cover:     { label: "Cover",            hint: "Copertina con immagine, logo e titolo" },
  about:     { label: "Chi sono",         hint: "Presentazione + foto profilo" },
  journey:   { label: "Il Percorso Bridal", hint: "4 capitoli narrativi: introduzione, prova, giorno del matrimonio, prodotti" },
  portfolio: { label: "Portfolio",        hint: "Galleria fino a 30 immagini" },
  quote:     { label: "Preventivo",       hint: "Blocco prezzi dinamico (auto-generato)" },
  contacts:  { label: "Contatti",         hint: "Telefono, email, social, indirizzo" },
};

const JOURNEY_BLOCKS = [
  { key: "intro_block",       label: "Introduzione",            hint: "Apri il racconto: chi sei, la tua filosofia, perché la sposa è in buone mani" },
  { key: "trial_block",       label: "La Prova",                hint: "Cosa accade durante la prova: tempi, atmosfera, cosa portare" },
  { key: "wedding_day_block", label: "Il Giorno del Matrimonio", hint: "Come si svolge il giorno-evento: arrivo, durata, ritocchi, accompagnamento" },
  { key: "products_block",    label: "I Prodotti Utilizzati",   hint: "Brand, qualità, durata, ipoallergenicità: cosa applichi sulla pelle/capelli" },
];

const MAX_PORTFOLIO = 30;
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024; // ~4 MB binary

/* ------------------------------------------------------------------ */
/* IMAGE UPLOAD — small inline component used by Cover/About/Portfolio */
/* ------------------------------------------------------------------ */
function ImageUploader({ value, onChange, kind, label, className = "" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const compressIfNeeded = (file) => new Promise((resolve, reject) => {
    // Quick client-side downscale for photos > 2 MB: caps width at 1600px
    // and re-encodes to JPEG 0.85 — keeps the upload payload well under
    // the 4 MB backend limit without requiring an external lib.
    if (file.size < 2 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const maxW = 1600;
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo immagini.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES * 1.5) {
      toast.error("Immagine troppo grande. Comprimila prima del caricamento.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await compressIfNeeded(file);
      const r = await api.post("/quote-template/me/upload", { data_url: dataUrl, kind });
      const ref = r.data?.ref || "";
      // Prefer the server-signed URL when available; data URL is the
      // immediate fallback to avoid a flash of broken image.
      const previewSrc = r.data?.url || dataUrl;
      onChange(ref, previewSrc);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={className}>
      {label && <Label className="label-uppercase mb-1.5 block">{label}</Label>}
      <div className="flex items-start gap-3">
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          className={`relative w-28 h-28 rounded-md border-2 border-dashed border-neutral-300 bg-neutral-50/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-neutral-400 transition-colors ${uploading ? "opacity-60 cursor-wait" : ""}`}
          data-testid={`qs-upload-${kind}`}
        >
          {value
            ? <img src={value} alt="" className="w-full h-full object-cover" />
            : <ImageIcon className="w-7 h-7 text-neutral-300" strokeWidth={1.4} />}
          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-500" strokeWidth={1.5} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 mt-1">
          <Button
            type="button" variant="outline" size="sm"
            className="rounded-full text-xs h-8"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`qs-upload-btn-${kind}`}
          >
            {uploading ? "Caricamento…" : value ? "Cambia" : "Carica"}
          </Button>
          {value && (
            <Button
              type="button" variant="ghost" size="sm"
              className="rounded-full text-xs h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
              onClick={() => onChange("", "")}
              data-testid={`qs-upload-remove-${kind}`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.6} />
              Rimuovi
            </Button>
          )}
          <p className="text-[10px] text-neutral-400 max-w-[160px] leading-tight">
            JPG / PNG · max 4 MB · compressione automatica
          </p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* SECTION CARD — wrapper with drag handle, toggle, reorder buttons     */
/* ------------------------------------------------------------------ */
function SectionCard({
  sectionKey, index, totalSections, enabled, dragHandlers,
  onToggle, onMove, children,
}) {
  const meta = SECTION_META[sectionKey];
  return (
    <div
      className="bg-white border border-border rounded-md overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
      data-testid={`qs-section-${sectionKey}`}
      {...dragHandlers}
    >
      <div className="flex items-center justify-between px-5 py-3 bg-neutral-50/40 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="cursor-grab text-neutral-300 hover:text-neutral-500" title="Trascina per riordinare">
            <GripVertical className="w-4 h-4" strokeWidth={1.6} />
          </span>
          <div>
            <h3 className="font-serif-heading text-base leading-tight">{meta.label}</h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">{meta.hint}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" onClick={() => onMove(index, index - 1)} disabled={index === 0}
            className="p-1 rounded hover:bg-neutral-200/50 disabled:opacity-30"
            title="Sposta su"
            data-testid={`qs-up-${sectionKey}`}
          >
            <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.8} />
          </button>
          <button
            type="button" onClick={() => onMove(index, index + 1)} disabled={index === totalSections - 1}
            className="p-1 rounded hover:bg-neutral-200/50 disabled:opacity-30"
            title="Sposta giù"
            data-testid={`qs-down-${sectionKey}`}
          >
            <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.8} />
          </button>
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            data-testid={`qs-toggle-${sectionKey}`}
          />
        </div>
      </div>
      {enabled && (
        <div className="px-5 py-4 space-y-4">{children}</div>
      )}
      {!enabled && (
        <div className="px-5 py-4 text-xs text-neutral-400 italic">Sezione nascosta — attiva l'interruttore per modificarla.</div>
      )}
    </div>
  );
}


/* ============================================================ */
/*                          MAIN PAGE                            */
/* ============================================================ */
export default function QuoteStudio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAiManager = (user?.plan || "").toLowerCase() === "ai_manager";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  // Local preview cache: { r2_ref: data_url }. Lets us show images we just
  // uploaded without waiting for the backend to round-trip a signed URL.
  const [previewMap, setPreviewMap] = useState({});
  // Mobile tab switch — "editor" | "preview". Desktop always shows both.
  const [viewMode, setViewMode] = useState("editor");
  // Iframe handle + nonce — bumping `previewNonce` forces a remount when
  // the user clicks "Aggiorna preview" (manual refresh fallback).
  const previewIframeRef = useRef(null);
  const [previewNonce, setPreviewNonce] = useState(0);
  const previewReadyRef = useRef(false);

  const previewUrl = (ref) => previewMap[ref] || ref || "";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/quote-template/me");
        setData(r.data?.template || null);
        setCanEdit(r.data?.can_edit === true);
        // Pre-load resolved (signed) URLs for any `r2://…` ref stored in
        // the template, so the editor can render previously-saved images
        // without waiting for a manual upload event.
        const resolved = r.data?.resolved_images || {};
        if (Object.keys(resolved).length > 0) {
          setPreviewMap((m) => ({ ...m, ...resolved }));
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Errore caricamento template");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (path, value) => {
    setData((d) => {
      if (!d) return d;
      const clone = JSON.parse(JSON.stringify(d));
      const parts = path.split(".");
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const moveSection = (from, to) => {
    if (to < 0 || to >= (data?.sections_order?.length || 0)) return;
    setData((d) => {
      const order = [...(d.sections_order || [])];
      const [item] = order.splice(from, 1);
      order.splice(to, 0, item);
      return { ...d, sections_order: order };
    });
  };

  // HTML5 drag&drop reordering — minimal, no external lib. We carry the
  // index in `dataTransfer` and reorder on drop. Touch devices still get
  // the ↑↓ buttons.
  const dragIdx = useRef(null);
  const dragHandlers = (idx) => ({
    draggable: true,
    onDragStart: (e) => { dragIdx.current = idx; e.dataTransfer.effectAllowed = "move"; },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
    onDrop: (e) => {
      e.preventDefault();
      if (dragIdx.current == null || dragIdx.current === idx) return;
      moveSection(dragIdx.current, idx);
      dragIdx.current = null;
    },
  });

  const save = async () => {
    if (!canEdit) {
      toast.error("Upgrade ad AI Manager™ per salvare il template.");
      return;
    }
    setSaving(true);
    try {
      // Strip non-server fields before submit
      const { _id, user_id, created_at, updated_at, ...payload } = data || {};  // eslint-disable-line no-unused-vars
      await api.put("/quote-template/me", payload);
      toast.success("Template salvato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  // Live preview sync — push state to the iframe whenever it changes.
  // The iframe listens via window.message; we also handle the "ready"
  // handshake so the first paint happens immediately on iframe load
  // (avoiding a flash of empty state).
  //
  // We mirror `data` + `previewMap` into refs so `pushToPreview` always
  // reads the LATEST state — without refs, the `onPreviewReady` callback
  // captured at mount-time would close over `data=null` and the iframe
  // would show the empty state until the next keystroke.
  const dataRef = useRef(data);
  const previewMapRef = useRef(previewMap);
  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { previewMapRef.current = previewMap; }, [previewMap]);

  const pushToPreview = () => {
    const win = previewIframeRef.current?.contentWindow;
    if (!win || !dataRef.current) return;
    try {
      // Parent and iframe are always same-origin — see
      // QuoteStudioPreview.jsx security note. Explicit targetOrigin
      // prevents leaking template/preview data to a spoofed iframe or
      // navigated-away document.
      win.postMessage(
        { type: "qs-preview-update", template: dataRef.current, previewMap: previewMapRef.current },
        window.location.origin,
      );
    } catch (_) {}
  };

  useEffect(() => {
    const trustedOrigin = window.location.origin;
    function onPreviewReady(e) {
      if (e.origin !== trustedOrigin) return;   // reject cross-origin handshake
      // Only trust the ready-handshake from the preview iframe we own.
      if (e.source !== previewIframeRef.current?.contentWindow) return;
      if (!e?.data || e.data.type !== "qs-preview-ready") return;
      previewReadyRef.current = true;
      pushToPreview();
    }
    window.addEventListener("message", onPreviewReady);
    return () => window.removeEventListener("message", onPreviewReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push on every change (cheap — postMessage is essentially free).
  useEffect(() => {
    if (!previewReadyRef.current) return;
    pushToPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, previewMap]);

  const refreshPreview = () => {
    previewReadyRef.current = false;
    setPreviewNonce((n) => n + 1);
  };

  const openInNewWindow = () => {
    const key = writePreviewSnapshot(data, previewMap);
    if (!key) {
      toast.error("Impossibile aprire l'anteprima (storage non disponibile).");
      return;
    }
    window.open(`/quote-studio/preview?k=${encodeURIComponent(key)}`, "_blank", "noopener");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" strokeWidth={1.4} />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-canvas">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-600"
              data-testid="qs-back"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent" strokeWidth={1.5} />
                <h1 className="font-serif-heading text-xl leading-tight truncate">Bridal Quote Studio™</h1>
                <span className="hidden sm:inline-block text-[10px] font-medium tracking-[0.12em] uppercase px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                  AI Manager™
                </span>
              </div>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                Personalizza il layout dei tuoi preventivi · 1 template per utente · auto-applicato a tutti i preventivi pubblici
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-2 mr-1">
              <Switch
                checked={!!data.enabled}
                onCheckedChange={(v) => setField("enabled", v)}
                disabled={!canEdit}
                data-testid="qs-master-toggle"
              />
              <span className="text-xs text-neutral-600">
                {data.enabled ? <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />Attivo</span>
                              : <span className="inline-flex items-center gap-1 text-neutral-400"><EyeOff className="w-3 h-3" />Bozza</span>}
              </span>
            </div>
            <Button
              onClick={save}
              disabled={!canEdit || saving}
              className="rounded-full"
              data-testid="qs-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Save className="w-4 h-4 mr-1.5" strokeWidth={1.6} />}
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        </div>
      </div>

      {/* Upgrade gate for non-AI-Manager users */}
      {!canEdit && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-6">
          <div className="bg-amber-50/70 border border-amber-200 rounded-md px-5 py-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <h3 className="font-medium text-amber-900 text-sm">Bridal Quote Studio™ è incluso solo nel piano AI Manager™</h3>
              <p className="text-xs text-amber-800/80 mt-1 leading-relaxed">
                Stai vedendo l'editor in modalità anteprima. Puoi esplorare le sezioni e i campi, ma non puoi salvare. Esegui l'upgrade per attivare la presentazione editoriale dei preventivi.
              </p>
              <Button
                size="sm" className="rounded-full mt-3"
                onClick={() => navigate("/app/plans")}
                data-testid="qs-upgrade-cta"
              >
                Scopri AI Manager™
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile tab switcher — desktop shows both panes side-by-side */}
      <div className="lg:hidden max-w-[1400px] mx-auto px-4 pt-4">
        <div className="inline-flex rounded-full border border-border bg-white p-1 text-xs font-medium" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "editor"}
            onClick={() => setViewMode("editor")}
            className={`px-4 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${viewMode === "editor" ? "bg-primary text-primary-foreground" : "text-neutral-600 hover:bg-neutral-100"}`}
            data-testid="qs-mobile-tab-editor"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.6} /> Editor
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "preview"}
            onClick={() => setViewMode("preview")}
            className={`px-4 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${viewMode === "preview" ? "bg-primary text-primary-foreground" : "text-neutral-600 hover:bg-neutral-100"}`}
            data-testid="qs-mobile-tab-preview"
          >
            <Monitor className="w-3.5 h-3.5" strokeWidth={1.6} /> Preview
          </button>
        </div>
      </div>

      {/* Split grid — editor left, preview right (desktop) */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* === EDITOR COLUMN === */}
          <div className={`${viewMode === "preview" ? "hidden lg:block" : ""} space-y-4 min-w-0`} data-testid="qs-editor-column">
            {(data.sections_order || []).map((key, idx) => {
          const sec = data[key] || {};
          const setSec = (field, value) => setField(`${key}.${field}`, value);
          const toggle = (v) => setSec("enabled", v);

          const common = {
            sectionKey: key, index: idx, totalSections: data.sections_order.length,
            enabled: !!sec.enabled, onToggle: toggle, onMove: moveSection,
            dragHandlers: dragHandlers(idx),
          };

          if (key === "cover") {
            return (
              <SectionCard key={key} {...common}>
                <ImageUploader
                  kind="cover"
                  label="Immagine di copertina"
                  value={previewUrl(sec.image_url)}
                  onChange={(ref, dataUrl) => {
                    if (ref && dataUrl) setPreviewMap((m) => ({ ...m, [ref]: dataUrl }));
                    setSec("image_url", ref);
                  }}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase">Titolo</Label>
                    <Input
                      value={sec.title || ""} onChange={(e) => setSec("title", e.target.value)}
                      className="mt-1 rounded-sm" placeholder="Es. Charmecheveux"
                      data-testid="qs-cover-title"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase">Sottotitolo</Label>
                    <Input
                      value={sec.subtitle || ""} onChange={(e) => setSec("subtitle", e.target.value)}
                      className="mt-1 rounded-sm" placeholder="Es. Bridal Hair Stylist"
                      data-testid="qs-cover-subtitle"
                    />
                  </div>
                </div>
                <ImageUploader
                  kind="logo"
                  label="Logo (opzionale — di default usa quello del brand)"
                  value={previewUrl(sec.logo_url)}
                  onChange={(ref, dataUrl) => {
                    if (ref && dataUrl) setPreviewMap((m) => ({ ...m, [ref]: dataUrl }));
                    setSec("logo_url", ref);
                  }}
                />
              </SectionCard>
            );
          }
          if (key === "about") {
            return (
              <SectionCard key={key} {...common}>
                <ImageUploader
                  kind="about"
                  label="Foto profilo"
                  value={previewUrl(sec.photo_url)}
                  onChange={(ref, dataUrl) => {
                    if (ref && dataUrl) setPreviewMap((m) => ({ ...m, [ref]: dataUrl }));
                    setSec("photo_url", ref);
                  }}
                />
                <div>
                  <Label className="label-uppercase">Titolo sezione</Label>
                  <Input
                    value={sec.heading || ""} onChange={(e) => setSec("heading", e.target.value)}
                    className="mt-1 rounded-sm" placeholder="Chi sono"
                    data-testid="qs-about-heading"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Testo</Label>
                  <Textarea
                    value={sec.body || ""} onChange={(e) => setSec("body", e.target.value)}
                    rows={6} className="mt-1 rounded-sm"
                    placeholder="Racconta chi sei, la tua esperienza, la tua filosofia di lavoro..."
                    data-testid="qs-about-body"
                  />
                  <div className="text-[10px] text-neutral-400 mt-1 text-right tabular-nums">
                    {(sec.body || "").length} / 4000
                  </div>
                </div>
              </SectionCard>
            );
          }
          if (key === "journey") {
            return (
              <SectionCard key={key} {...common}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase">Titolo sezione</Label>
                    <Input
                      value={sec.heading || ""} onChange={(e) => setSec("heading", e.target.value)}
                      className="mt-1 rounded-sm" placeholder="Il Percorso Bridal"
                      data-testid="qs-journey-heading"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase">Eyebrow (sopra titolo)</Label>
                    <Input
                      value={sec.eyebrow || ""} onChange={(e) => setSec("eyebrow", e.target.value)}
                      className="mt-1 rounded-sm" placeholder="Il viaggio insieme"
                      data-testid="qs-journey-eyebrow"
                    />
                  </div>
                </div>
                {JOURNEY_BLOCKS.map((blk) => {
                  const block = sec[blk.key] || {};
                  const setBlock = (field, value) => setField(`journey.${blk.key}.${field}`, value);
                  return (
                    <div
                      key={blk.key}
                      className="border border-neutral-200 rounded-md p-4 bg-neutral-50/30"
                      data-testid={`qs-journey-${blk.key}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-serif-heading text-sm leading-tight">{blk.label}</h4>
                          <p className="text-[11px] text-neutral-500 mt-0.5">{blk.hint}</p>
                        </div>
                        <Switch
                          checked={block.enabled !== false}
                          onCheckedChange={(v) => setBlock("enabled", v)}
                          data-testid={`qs-journey-${blk.key}-toggle`}
                        />
                      </div>
                      {(block.enabled !== false) && (
                        <div className="space-y-3">
                          <ImageUploader
                            kind={`journey-${blk.key}`}
                            label="Immagine (opzionale)"
                            value={previewUrl(block.image_url)}
                            onChange={(ref, dataUrl) => {
                              if (ref && dataUrl) setPreviewMap((m) => ({ ...m, [ref]: dataUrl }));
                              setBlock("image_url", ref);
                            }}
                          />
                          <div>
                            <Label className="label-uppercase">Titolo del capitolo</Label>
                            <Input
                              value={block.heading || ""} onChange={(e) => setBlock("heading", e.target.value)}
                              className="mt-1 rounded-sm" placeholder={blk.label}
                              data-testid={`qs-journey-${blk.key}-heading`}
                            />
                          </div>
                          <div>
                            <Label className="label-uppercase">Testo del capitolo</Label>
                            <Textarea
                              value={block.body || ""} onChange={(e) => setBlock("body", e.target.value)}
                              rows={5} className="mt-1 rounded-sm"
                              placeholder="Racconta questo passaggio del percorso con la sposa..."
                              data-testid={`qs-journey-${blk.key}-body`}
                            />
                            <div className="text-[10px] text-neutral-400 mt-1 text-right tabular-nums">
                              {(block.body || "").length} / 4000
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </SectionCard>
            );
          }
          if (key === "portfolio") {
            return (
              <SectionCard key={key} {...common}>
                <div>
                  <Label className="label-uppercase">Titolo sezione</Label>
                  <Input
                    value={sec.heading || ""} onChange={(e) => setSec("heading", e.target.value)}
                    className="mt-1 rounded-sm" placeholder="Portfolio"
                    data-testid="qs-portfolio-heading"
                  />
                </div>
                <PortfolioGrid
                  images={sec.images || []}
                  previewMap={previewMap}
                  onAddRef={(ref, dataUrl) => {
                    setPreviewMap((m) => ({ ...m, [ref]: dataUrl }));
                    setSec("images", [...(sec.images || []), { url: ref, caption: "" }]);
                  }}
                  onRemove={(i) => setSec("images", (sec.images || []).filter((_, ii) => ii !== i))}
                  onReorder={(arr) => setSec("images", arr)}
                  onCaption={(i, caption) => {
                    const arr = [...(sec.images || [])];
                    arr[i] = { ...arr[i], caption };
                    setSec("images", arr);
                  }}
                />
              </SectionCard>
            );
          }
          if (key === "quote") {
            return (
              <SectionCard key={key} {...common}>
                <div className="bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3">
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    Questa sezione è <strong>generata automaticamente</strong> dal sistema usando i dati della scheda Preventivo del cliente: servizi, prezzi, optional, trasferta, acconto e totale.
                    Non viene compilata manualmente.
                  </p>
                </div>
                <div>
                  <Label className="label-uppercase">Titolo sezione</Label>
                  <Input
                    value={sec.heading || ""} onChange={(e) => setSec("heading", e.target.value)}
                    className="mt-1 rounded-sm" placeholder="Preventivo"
                    data-testid="qs-quote-heading"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Introduzione (opzionale)</Label>
                  <Textarea
                    value={sec.intro || ""} onChange={(e) => setSec("intro", e.target.value)}
                    rows={3} className="mt-1 rounded-sm"
                    placeholder="Es. Ecco il preventivo dedicato per il vostro grande giorno..."
                    data-testid="qs-quote-intro"
                  />
                </div>
              </SectionCard>
            );
          }
          if (key === "contacts") {
            return (
              <SectionCard key={key} {...common}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase">Telefono</Label>
                    <Input
                      value={sec.phone || ""} onChange={(e) => setSec("phone", e.target.value)}
                      className="mt-1 rounded-sm" data-testid="qs-contacts-phone"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase">Email</Label>
                    <Input
                      value={sec.email || ""} onChange={(e) => setSec("email", e.target.value)}
                      type="email" className="mt-1 rounded-sm" data-testid="qs-contacts-email"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase">Instagram</Label>
                    <Input
                      value={sec.instagram || ""} onChange={(e) => setSec("instagram", e.target.value)}
                      className="mt-1 rounded-sm" placeholder="@username" data-testid="qs-contacts-instagram"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase">Sito web</Label>
                    <Input
                      value={sec.website || ""} onChange={(e) => setSec("website", e.target.value)}
                      className="mt-1 rounded-sm" placeholder="https://..." data-testid="qs-contacts-website"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="label-uppercase">Indirizzo (opzionale)</Label>
                    <Input
                      value={sec.address || ""} onChange={(e) => setSec("address", e.target.value)}
                      className="mt-1 rounded-sm" data-testid="qs-contacts-address"
                    />
                  </div>
                </div>
              </SectionCard>
            );
          }
          return null;
        })}

        {/* Theme */}
        <div className="bg-white border border-border rounded-md overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="px-5 py-3 bg-neutral-50/40 border-b border-border">
            <h3 className="font-serif-heading text-base leading-tight">Tema & Colori</h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Personalizza ogni superficie della brochure. Le modifiche si applicano subito al preventivo pubblico.
            </p>
          </div>
          <div className="px-5 py-4 space-y-5">
            <div>
              <Label className="label-uppercase">Tipografia</Label>
              <select
                value={data.theme?.font_family || "serif"}
                onChange={(e) => setField("theme.font_family", e.target.value)}
                className="mt-1 w-full max-w-xs rounded-sm border border-input h-10 px-3 text-sm bg-background"
                data-testid="qs-theme-font"
              >
                <option value="serif">Serif — editoriale</option>
                <option value="sans">Sans-serif — moderna</option>
              </select>
            </div>

            <ColorGrid
              data={data}
              setField={setField}
              groupTitle="Cover"
              fields={[
                { path: "theme.primary_color",  label: "Sfondo cover (scuro)",   default: "#0f172a", testId: "qs-theme-primary" },
              ]}
            />
            <ColorGrid
              data={data}
              setField={setField}
              groupTitle="Pagina"
              fields={[
                { path: "theme.page_bg",        label: "Sfondo pagina",           default: "#ffffff", testId: "qs-theme-page-bg" },
                { path: "theme.section_bg_alt", label: "Sfondo banda alternata",  default: "#f8f6f2", testId: "qs-theme-section-bg-alt" },
                { path: "theme.text_primary",   label: "Testo principale",        default: "#1f1d1a", testId: "qs-theme-text-primary" },
              ]}
            />
            <ColorGrid
              data={data}
              setField={setField}
              groupTitle="Titoli & accenti"
              fields={[
                { path: "theme.section_title",  label: "Titoli di sezione",       default: "#0f172a", testId: "qs-theme-section-title" },
                { path: "theme.subtitle_color", label: "Sottotitoli / eyebrow",   default: "#b8895a", testId: "qs-theme-subtitle" },
                { path: "theme.divider_color",  label: "Linee divisorie",         default: "#b8895a", testId: "qs-theme-divider" },
                { path: "theme.accent_color",   label: "Colore accento oro",      default: "#b8895a", testId: "qs-theme-accent" },
              ]}
            />
            <ColorGrid
              data={data}
              setField={setField}
              groupTitle="Bottoni"
              fields={[
                { path: "theme.button_bg",   label: "Sfondo bottoni",  default: "#b8895a", testId: "qs-theme-button-bg" },
                { path: "theme.button_text", label: "Testo bottoni",   default: "#ffffff", testId: "qs-theme-button-text" },
              ]}
            />
          </div>
        </div>

        <div className="text-xs text-neutral-400 text-center pt-4">
          Modifiche salvate vengono applicate immediatamente a tutti i preventivi pubblici condivisi.
        </div>
          </div>
          {/* === PREVIEW COLUMN === */}
          <div
            className={`${viewMode === "editor" ? "hidden lg:block" : ""} lg:sticky lg:top-[88px] min-w-0`}
            data-testid="qs-preview-column"
          >
            <PreviewPane
              iframeRef={previewIframeRef}
              nonce={previewNonce}
              onRefresh={refreshPreview}
              onOpenNewWindow={openInNewWindow}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* PREVIEW PANE — sticky right column showing the live brochure render  */
/* ------------------------------------------------------------------ */
function PreviewPane({ iframeRef, nonce, onRefresh, onOpenNewWindow }) {
  return (
    <div
      className="bg-white border border-border rounded-md overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)] flex flex-col"
      style={{ height: "calc(100vh - 110px)", minHeight: 540 }}
      data-testid="qs-preview-pane"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-50/40 border-b border-border shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="relative inline-flex items-center justify-center w-2 h-2"
            aria-hidden
          >
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[11px] font-medium tracking-[0.08em] uppercase text-neutral-700" data-testid="qs-preview-indicator">
            Anteprima in tempo reale
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-900 px-2 py-1 rounded hover:bg-neutral-100"
            title="Aggiorna preview"
            data-testid="qs-preview-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />
            <span className="hidden sm:inline">Aggiorna</span>
          </button>
          <button
            type="button"
            onClick={onOpenNewWindow}
            className="inline-flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-900 px-2 py-1 rounded hover:bg-neutral-100"
            title="Apri in nuova finestra"
            data-testid="qs-preview-open-new"
          >
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.6} />
            <span className="hidden sm:inline">Apri in nuova finestra</span>
          </button>
        </div>
      </div>
      {/* Iframe — owns its own viewport so `60vh` etc render at the
          iframe's size, not the editor browser tab's full screen. */}
      <iframe
        key={nonce}
        ref={iframeRef}
        title="Anteprima preventivo"
        src="/quote-studio/preview"
        className="flex-1 w-full border-0 bg-white"
        data-testid="qs-preview-iframe"
      />
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* PORTFOLIO GRID — multi-image uploader with reorder + captions       */
/* ------------------------------------------------------------------ */
function PortfolioGrid({ images, previewMap, onAddRef, onRemove, onReorder, onCaption }) {
  const fileInputRef = useRef(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const dragIdx = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const remaining = MAX_PORTFOLIO - images.length;
    if (remaining <= 0) {
      toast.error(`Massimo ${MAX_PORTFOLIO} immagini.`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    setUploadingCount(list.length);
    for (const file of list) {
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          if (file.size < 2 * 1024 * 1024) {
            const r = new FileReader();
            r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
          } else {
            // Reuse the same compression logic as ImageUploader inline
            const img = new Image();
            const r = new FileReader();
            r.onload = () => { img.src = r.result; };
            r.onerror = reject;
            img.onload = () => {
              const scale = Math.min(1, 1600 / img.width);
              const canvas = document.createElement("canvas");
              canvas.width = Math.round(img.width * scale);
              canvas.height = Math.round(img.height * scale);
              canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL("image/jpeg", 0.85));
            };
            img.onerror = reject;
            r.readAsDataURL(file);
          }
        });
        const r = await api.post("/quote-template/me/upload", { data_url: dataUrl, kind: "portfolio" });
        const previewSrc = r.data?.url || dataUrl;
        onAddRef(r.data?.ref, previewSrc);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload fallito per un'immagine");
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-neutral-500 tabular-nums">
          {images.length} / {MAX_PORTFOLIO} immagini
        </span>
        <Button
          type="button" size="sm" variant="outline"
          className="rounded-full text-xs h-8"
          onClick={() => fileInputRef.current?.click()}
          disabled={images.length >= MAX_PORTFOLIO}
          data-testid="qs-portfolio-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.6} />
          Aggiungi immagini
        </Button>
        <input
          ref={fileInputRef}
          type="file" accept="image/*" multiple className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {images.length === 0 && uploadingCount === 0 ? (
        <div className="py-10 text-center text-xs text-neutral-400 border-2 border-dashed border-neutral-200 rounded-md">
          Nessuna immagine ancora caricata.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="qs-portfolio-grid">
          {images.map((im, idx) => {
            const src = previewMap[im.url] || im.url;
            return (
              <div
                key={`${im.url}-${idx}`}
                className="relative group rounded-md overflow-hidden border border-border bg-white"
                draggable
                onDragStart={() => { dragIdx.current = idx; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIdx.current == null || dragIdx.current === idx) return;
                  const arr = [...images];
                  const [item] = arr.splice(dragIdx.current, 1);
                  arr.splice(idx, 0, item);
                  onReorder(arr);
                  dragIdx.current = null;
                }}
                data-testid={`qs-portfolio-item-${idx}`}
              >
                <div className="aspect-square">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 hover:bg-rose-50 text-rose-700 border border-rose-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Rimuovi"
                  data-testid={`qs-portfolio-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                </button>
                <input
                  value={im.caption || ""}
                  onChange={(e) => onCaption(idx, e.target.value)}
                  placeholder="Didascalia (opzionale)"
                  className="w-full text-[11px] px-2 py-1.5 border-t border-border bg-white outline-none"
                  data-testid={`qs-portfolio-caption-${idx}`}
                />
              </div>
            );
          })}
          {Array.from({ length: uploadingCount }).map((_, i) => (
            <div key={`up-${i}`} className="aspect-square rounded-md border-2 border-dashed border-neutral-200 flex items-center justify-center bg-neutral-50">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-300" strokeWidth={1.4} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* COLOR GRID — small helper for the granular Theme editor             */
/* ------------------------------------------------------------------ */
function ColorGrid({ data, setField, groupTitle, fields }) {
  const getVal = (path) => {
    const parts = path.split(".");
    let cur = data;
    for (const p of parts) cur = (cur || {})[p];
    return cur || "";
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 mb-2">{groupTitle}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {fields.map((f) => (
          <div key={f.path}>
            <Label className="label-uppercase text-[10px]">{f.label}</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={getVal(f.path) || f.default}
                onChange={(e) => setField(f.path, e.target.value)}
                className="w-9 h-9 rounded-md border border-border bg-transparent cursor-pointer shrink-0"
                data-testid={f.testId}
              />
              <Input
                value={getVal(f.path)}
                onChange={(e) => setField(f.path, e.target.value)}
                className="rounded-sm flex-1 font-mono text-[11px] h-9"
                placeholder={f.default}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
